import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

/**
 * Conversation forms configured in Admin → Conversation Management → Forms.
 * The agent picks one when creating a conversation (mirrors the "Interaction
 * Form" selector in the KC2 reference form). Degrades to an empty list if the
 * caller lacks the admin-view permission.
 */
export interface ConversationForm {
  id: string
  name: string
  isDefault?: boolean
}

export interface UseConversationFormsResult {
  forms: ConversationForm[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useConversationForms(fetchOnMount = false): UseConversationFormsResult {
  const [forms, setForms] = useState<ConversationForm[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async (): Promise<void> => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<{ forms: ConversationForm[] }>('/admin/conversation-management/forms/dropdown')
      setForms(res.forms ?? [])
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to fetch conversation forms')
      setForms([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (fetchOnMount) void refetch()
  }, [fetchOnMount, refetch])

  return { forms, loading, error, refetch }
}
